import { useRef, useState } from 'react';

import { captureEvent } from '@/services/analytics';
import { TableCell, TableRow, useTheme } from '@/shared/ui';

import { Permission } from '@/entities/permissions';
import type { SymbolKey } from '@/entities/symbol';
import { usePermission } from '@/features/_shared/user';
import DateCell from '@/features/markets/components/shared/DateCell';
import ExpandCell from '@/features/markets/components/shared/ExpandCell';
import { getRowBackgroundColor } from '@/features/markets/components/shared/tableHelpers';
import { dataRowSx } from '@/features/markets/components/shared/tableStyles';
import ValueCell from '@/features/markets/components/shared/ValueCell';
import { useSelectionFunctions } from '@/features/symbol-selector';
import { formatNumberWithPrecision } from '@/shared/lib/format/formatNumberWithPrecision';
import { getDiffColor } from '@/shared/lib/format/getDiffColor';
import CustomCheckbox from '@/shared/ui/primitives/Checkbox';
import IndeterminateCheckbox from '@/shared/ui/primitives/Checkbox/IndeterminateCheckbox';

import useCommoditiesSelection from '../../useCommoditiesSelection';
import CollapsedRow from './CollapsedRow';

import type {
  CommoditiesTableRowType,
  CommoditiesTableSectionType
} from '@/features/markets/types';

type RowCheckboxesProps = {
  row: CommoditiesTableRowType;
  lastSymbol: SymbolKey;
  diffSymbol: SymbolKey;
  restrictedData: boolean;
  isSpotLocked: boolean;
  isSymbolSelected: (symbol: SymbolKey | undefined) => boolean;
  toggleCodeInSelection: (row: CommoditiesTableRowType, bothSelected: boolean) => void;
  toggleSymbolInSelection: (symbol: SymbolKey) => void;
};

const RowCheckboxes = ({
  row,
  lastSymbol,
  diffSymbol,
  restrictedData,
  isSpotLocked,
  isSymbolSelected,
  toggleCodeInSelection,
  toggleSymbolInSelection
}: RowCheckboxesProps) => {
  if (lastSymbol.Code && diffSymbol.Code) {
    const bothSelected = isSymbolSelected(lastSymbol) && isSymbolSelected(diffSymbol);
    return (
      <IndeterminateCheckbox
        disabled={restrictedData}
        firstSelected={isSymbolSelected(lastSymbol)}
        secondSelected={isSymbolSelected(diffSymbol)}
        modeLocked={isSpotLocked}
        onLockedClick={() => {
          toggleCodeInSelection(row, bothSelected);
        }}
        onChange={() => {
          toggleCodeInSelection(row, bothSelected);
        }}
      />
    );
  }
  if (lastSymbol.Code) {
    return (
      <CustomCheckbox
        checked={isSymbolSelected(lastSymbol)}
        disabled={restrictedData}
        modeLocked={isSpotLocked}
        onLockedClick={() => {
          toggleSymbolInSelection(lastSymbol);
        }}
        onChange={() => {
          toggleSymbolInSelection(lastSymbol);
        }}
      />
    );
  }
  if (diffSymbol.Code) {
    return (
      <CustomCheckbox
        checked={isSymbolSelected(diffSymbol)}
        disabled={restrictedData}
        modeLocked={isSpotLocked}
        onLockedClick={() => {
          toggleSymbolInSelection(diffSymbol);
        }}
        onChange={() => {
          toggleSymbolInSelection(diffSymbol);
        }}
      />
    );
  }
  return null;
};

const CommoditiesTableRow = ({
  row,
  sections,
  index,
  isLoading = false,
  disableSelection = false,
  shouldRenderDiff = true,
  shouldRenderDiffChange = true,
  shouldRenderLast = true,
  shouldRenderLastChange = true
}: {
  row: CommoditiesTableRowType;
  sections: CommoditiesTableSectionType[];
  index: number;
  isLoading?: boolean;
  disableSelection?: boolean;
  shouldRenderDiff?: boolean;
  shouldRenderDiffChange?: boolean;
  shouldRenderLast?: boolean;
  shouldRenderLastChange?: boolean;
}) => {
  const theme = useTheme();
  const {
    shortTitle,
    date,
    last,
    delta,
    diff,
    diffDelta,
    symbol: { key },
    diffSymbol: { key: diffKey }
  } = row;
  const lastSymbol = key;
  const diffSymbol = diffKey;
  const { hasPermission } = usePermission();
  const isRestricted = !hasPermission(Permission.ViewMarketScreens);
  const restrictedData = (!last && !diff) || isRestricted;
  const rowRef = useRef<HTMLTableRowElement | null>(null);
  const backgroundColor = getRowBackgroundColor(restrictedData, index, theme);

  const [open, setOpen] = useState(false);
  const { isSymbolSelected, toggleSymbolInSelection, isSpotLocked } = useSelectionFunctions();
  const { toggleCodeInSelection } = useCommoditiesSelection(sections);

  const lastDisplayValue = last ? formatNumberWithPrecision(last) : '';
  const deltaDisplayValue = delta ? formatNumberWithPrecision(delta) : '';
  const diffDisplayValue = diff ? formatNumberWithPrecision(diff) : '';
  const diffDeltaDisplayValue = diffDelta ? formatNumberWithPrecision(diffDelta) : '';

  return (
    <>
      <TableRow
        key={shortTitle}
        ref={rowRef}
        className='markets-table-row'
        sx={{ ...dataRowSx, backgroundColor }}
        onClick={() => {
          captureEvent('table_row_click', {
            symbol: row.symbol,
            diffSymbol: row.diffSymbol
          });
        }}
      >
        <ExpandCell
          open={open}
          disabled={restrictedData}
          onToggle={() => {
            setOpen(!open);
          }}
        >
          {!disableSelection && (
            <RowCheckboxes
              row={row}
              lastSymbol={lastSymbol}
              diffSymbol={diffSymbol}
              restrictedData={restrictedData}
              isSpotLocked={isSpotLocked}
              isSymbolSelected={isSymbolSelected}
              toggleCodeInSelection={toggleCodeInSelection}
              toggleSymbolInSelection={toggleSymbolInSelection}
            />
          )}
        </ExpandCell>
        <TableCell
          component='th'
          scope='row'
          sx={{
            display: 'flex',
            alignItems: 'center'
          }}
        >
          {shortTitle}
        </TableCell>
        <DateCell
          date={date}
          isLoading={isLoading}
        />
        {shouldRenderLast && (
          <ValueCell
            isLoading={isLoading}
            hasValue={Boolean(last)}
            displayValue={lastDisplayValue}
            restricted={restrictedData}
          />
        )}
        {shouldRenderLastChange && (
          <ValueCell
            isLoading={isLoading}
            hasValue={Boolean(delta)}
            displayValue={deltaDisplayValue}
            restricted={isRestricted}
            sx={{ color: getDiffColor(Number(delta)) }}
          />
        )}
        {shouldRenderDiff && (
          <ValueCell
            isLoading={isLoading}
            hasValue={Boolean(diff)}
            displayValue={diffDisplayValue}
            restricted={restrictedData}
          />
        )}
        {shouldRenderDiffChange && (
          <ValueCell
            isLoading={isLoading}
            hasValue={Boolean(diffDelta)}
            displayValue={diffDeltaDisplayValue}
            restricted={restrictedData}
            sx={{ color: isRestricted ? '' : getDiffColor(Number(diffDelta)) }}
          />
        )}
      </TableRow>
      <TableRow
        sx={{
          backgroundColor
        }}
      >
        <CollapsedRow
          diffSymbol={diffSymbol}
          hideInfo={disableSelection}
          lastSymbol={lastSymbol}
          open={open}
          row={row}
        />
      </TableRow>
    </>
  );
};

export default CommoditiesTableRow;
